import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { LOGIN_HELP_VIDEOS } from '../data/loginHelpVideos';
import './LoginHelpBot.css';

function firstPlayableVideo() {
  return LOGIN_HELP_VIDEOS.find((v) => !v.comingSoon && v.src) || LOGIN_HELP_VIDEOS[0];
}

/** @param {HTMLVideoElement | null} video */
function describeMediaError(video) {
  const err = video?.error;
  if (!err) return null;
  const M = typeof MediaError !== 'undefined' ? MediaError : {};
  const aborted = M.MEDIA_ERR_ABORTED ?? 1;
  const network = M.MEDIA_ERR_NETWORK ?? 2;
  const decode = M.MEDIA_ERR_DECODE ?? 3;
  const notSupported = M.MEDIA_ERR_SRC_NOT_SUPPORTED ?? 4;
  switch (err.code) {
    case aborted:
      return 'Playback was aborted.';
    case network:
      return 'Network error while loading. Open the video URL in a new tab — if you see 404, the file is not on the server or the path is wrong.';
    case decode:
      return 'The file could not be decoded. The file may be corrupt, or it may use a codec this browser cannot play.';
    case notSupported:
    default:
      return 'This file uses a format or codec your browser cannot play. Re-export as MP4 with H.264 (AVC) video and AAC audio. Avoid HEVC/H.265, ProRes, or “High Efficiency” exports from phones.';
  }
}

export default function LoginHelpBot() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(() => firstPlayableVideo()?.id || LOGIN_HELP_VIDEOS[0]?.id);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const titleId = useId();
  const [videoLoadError, setVideoLoadError] = useState(false);
  const [mediaErrorHint, setMediaErrorHint] = useState(null);

  const active = LOGIN_HELP_VIDEOS.find((v) => v.id === activeId) || LOGIN_HELP_VIDEOS[0];

  useEffect(() => {
    setVideoLoadError(false);
    setMediaErrorHint(null);
  }, [activeId]);

  const handleClose = useCallback(() => {
    setOpen(false);
    const v = panelRef.current?.querySelector('video');
    if (v) {
      v.pause();
    }
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    const first = firstPlayableVideo();
    if (first) setActiveId(first.id);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      const closeBtn = panelRef.current?.querySelector('.login-help-bot-close');
      closeBtn?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onFocus = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) {
        panelRef.current.querySelector('.login-help-bot-close')?.focus();
      }
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, [open]);

  const showPlayer = active && !active.comingSoon && active.src;

  return (
    <div className="login-help-bot-root">
      <button
        type="button"
        ref={triggerRef}
        className="login-help-bot-trigger"
        onClick={() => (open ? handleClose() : handleOpen())}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? 'login-help-bot-panel' : undefined}
        aria-label="Need help? Open help videos"
      >
        <span className="login-help-bot-bubble" aria-hidden="true">
          Need help?
        </span>
        <span className="login-help-bot-fab" aria-hidden="true">
          <HelpCircle size={22} strokeWidth={2} />
        </span>
      </button>

      {open && (
        <div
          className="login-help-bot-overlay"
          onClick={handleClose}
          role="presentation"
        >
          <div
            id="login-help-bot-panel"
            ref={panelRef}
            className="login-help-bot-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="login-help-bot-header">
              <h2 id={titleId} className="login-help-bot-title">
                Help videos
              </h2>
              <button
                type="button"
                className="login-help-bot-close"
                onClick={handleClose}
                aria-label="Close help"
              >
                <X size={20} strokeWidth={2} />
              </button>
            </div>
            {LOGIN_HELP_VIDEOS.length > 1 && (
              <ul className="login-help-bot-list">
                {LOGIN_HELP_VIDEOS.map((item) => {
                  const selected = item.id === activeId;
                  return (
                    <li key={item.id} className="login-help-bot-list-item">
                      <button
                        type="button"
                        className={`login-help-bot-topic${selected ? ' login-help-bot-topic--active' : ''}`}
                        onClick={() => setActiveId(item.id)}
                        aria-current={selected ? 'true' : undefined}
                        disabled={item.comingSoon}
                      >
                        <span className="login-help-bot-topic-title">{item.title}</span>
                        {item.comingSoon && (
                          <span className="login-help-bot-soon">Soon</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {active && (
              <div className="login-help-bot-detail">
                <h3 className="login-help-bot-video-title">{active.title}</h3>
                {showPlayer ? (
                  <div className="login-help-bot-video-wrap">
                    {videoLoadError && (
                      <div className="login-help-bot-video-error" role="alert">
                        <strong>Video did not load.</strong>
                        {mediaErrorHint && (
                          <p className="login-help-bot-video-error-detail">{mediaErrorHint}</p>
                        )}
                        <ul>
                          <li>
                            <strong>Plays on your phone but not on this browser?</strong> Phones (especially iPhone Safari) often play <strong>HEVC / &quot;High Efficiency&quot;</strong> MP4s that <strong>Chrome and Edge on Windows do not</strong> in the web player. Re-encode the same clip as <strong>H.264 (AVC) Baseline or Main</strong> profile + <strong>AAC</strong> audio, then replace the file and redeploy.
                          </li>
                          <li>
                            If you just replaced the file: hard-refresh the page (Ctrl+Shift+R) so the browser does not use a cached MP4.
                          </li>
                          <li>
                            Put the file at <code>client/public/help-videos/</code> with the name from <code>loginHelpVideos.js</code> (exact spelling; case-sensitive on Linux servers).
                          </li>
                          <li>
                            Run <code>npm run build</code> and redeploy so <code>build/help-videos/</code> contains the file.
                          </li>
                          <li>
                            Open <code>{active.src}</code> in a new tab: you should get a playable video or a download. <strong>404</strong> = file not on the server (rebuild client, copy build/help-videos/ to the host, restart). If the <strong>login page or HTML</strong> appears instead of a video, the MP4 was not in the deployed build (missing files used to return the SPA HTML; deploy the latest server so missing MP4 returns 404 instead).
                          </li>
                          <li>
                            Encode for the web: <strong>H.264 (AVC)</strong> video + <strong>AAC</strong> audio - not AC3/DTS. Try main/baseline profile if High profile fails. HandBrake Web preset or FFmpeg.
                          </li>
                        </ul>
                      </div>
                    )}
                    <video
                      key={active.id}
                      className={`login-help-bot-video${videoLoadError ? ' login-help-bot-video--hidden' : ''}`}
                      controls
                      preload="metadata"
                      playsInline
                      src={active.src}
                      aria-label={active.title}
                      onError={(e) => {
                        setVideoLoadError(true);
                        setMediaErrorHint(describeMediaError(e.currentTarget));
                      }}
                      onLoadedData={() => {
                        setVideoLoadError(false);
                        setMediaErrorHint(null);
                      }}
                    >
                      Your browser does not support embedded video.
                    </video>
                  </div>
                ) : (
                  <p className="login-help-bot-na">This video is not available yet.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
